/**
 * EO «Конфигуратор» — корень вкладки: 6 суб-табов «Филиалы | Квесты и слоты |
 * Цены | Услуги | Справочники | Спецверсии» (A33/A35/A38; паттерн
 * OoSalaryView; активный — purple по правилу A2). Неактивные табы показывают
 * короткое имя, активный — полное (эталон screen-17 js:106-107). Все 6 живые:
 * branches, quests, prices, services (A38: авто-сейв, без dirty-guard'а),
 * dicts (A44: модель услуг, без dirty-guard'а — Д4), specs.
 *
 * Guard несохранённой правки: dirty приходит из дочерней панели (открытый
 * inline-ряд чайной у Филиалов, несохранённая сетка слотов у Квестов); смена
 * суб-таба при dirty — через кастомную confirm-модалку (A3): «Выйти» =
 * discard (размонтирование ветки) + переход, «Отмена» = остаться. Квесты
 * прилагают подпись (имя квеста) — модалка называет сетку (эталон js:95),
 * generic-текст остаётся фолбэком для панелей без подписи (Филиалы).
 */
import { useCallback, useState } from "react";
import { EoConfigBranches } from "./EoConfigBranches";
import { EoConfigDicts } from "./EoConfigDicts";
import { EoConfigPrices } from "./EoConfigPrices";
import { EoConfigQuests } from "./EoConfigQuests";
import { EoConfigServices } from "./EoConfigServices";
import { EoConfigSpecs } from "./EoConfigSpecs";
import { CfgModal } from "./EoConfigModal";
import css from "./config.module.css";

type SubTab = "branches" | "quests" | "prices" | "services" | "dicts" | "specs";

const SUBTABS: { id: SubTab; full: string; short: string }[] = [
  { id: "branches", full: "Филиалы", short: "Филиалы" },
  { id: "quests", full: "Квесты", short: "Квесты" },
  { id: "prices", full: "Цены", short: "Цены" },
  { id: "services", full: "Услуги", short: "Услуги" },
  { id: "dicts", full: "Справочники", short: "Справ." },
  { id: "specs", full: "Спецверсии", short: "Спец" },
];

export function EoConfigView() {
  const [tab, setTab] = useState<SubTab>("branches");
  const [dirty, setDirty] = useState(false);
  const [dirtyLabel, setDirtyLabel] = useState<string | null>(null);
  const [pendingTab, setPendingTab] = useState<SubTab | null>(null);

  const onDirty = useCallback((d: boolean, label?: string) => {
    setDirty(d);
    setDirtyLabel(d ? label ?? null : null);
  }, []);

  const pick = (t: SubTab) => {
    if (t === tab) return;
    if (dirty) setPendingTab(t);
    else setTab(t);
  };

  return (
    <div className={css.wrap}>
      {/* data-cfg-guard: тап по суб-табам не коммитит открытую правку —
          решение о её судьбе принимает confirm-модалка ниже */}
      <nav className={css.subtabs} aria-label="Разделы конфигуратора" data-cfg-guard="">
        {SUBTABS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`${css.stab} ${tab === s.id ? css.stabOn : ""}`}
            onClick={() => pick(s.id)}
          >
            {tab === s.id ? s.full : s.short}
          </button>
        ))}
      </nav>
      {tab === "branches" ? (
        <EoConfigBranches onDirtyChange={onDirty} />
      ) : tab === "quests" ? (
        <EoConfigQuests onDirtyChange={onDirty} />
      ) : tab === "prices" ? (
        /* A38: авто-сейв по ячейке — dirty-скоупа нет, onDirtyChange не нужен */
        <EoConfigPrices />
      ) : tab === "services" ? (
        <EoConfigServices onDirtyChange={onDirty} />
      ) : tab === "dicts" ? (
        <EoConfigDicts />
      ) : (
        <EoConfigSpecs />
      )}
      {pendingTab ? (
        <CfgModal
          tone="warn"
          title={dirtyLabel ? "Сетка не сохранена" : "Несохранённая правка"}
          actionLabel="Выйти"
          onAction={() => {
            onDirty(false); // ветка размонтируется — правка отброшена
            setTab(pendingTab);
            setPendingTab(null);
          }}
          onCancel={() => setPendingTab(null)}
        >
          {dirtyLabel ? (
            <>Сетка «<b>{dirtyLabel}</b>» не сохранена — выйти без сохранения?</>
          ) : (
            "Есть несохранённая правка — выйти без сохранения?"
          )}
        </CfgModal>
      ) : null}
    </div>
  );
}
